import {
  Cancel as CancelIcon,
} from '../../icons';
import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { usePublicTheme } from '../../../contexts/PublicThemeContext';


// Continuous shimmer animation for loading effect
const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

// Wave animation for active processing
const wave = keyframes`
  0% {
    transform: translateX(-100%) scaleX(1);
  }
  50% {
    transform: translateX(0%) scaleX(1.2);
  }
  100% {
    transform: translateX(100%) scaleX(1);
  }
`;

// Gentle glow for ready status
const glow = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
`;

// Completion pulse
const completionPulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.02);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Styled components
const ProgressContainer = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  margin: theme.spacing(2, 0),
  overflow: 'hidden',
}));

const GradientHeader = styled(Box)(({ theme, primarycolor }) => ({
  background: primarycolor
    ? `linear-gradient(135deg, ${primarycolor} 0%, ${primarycolor}dd 100%)`
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: theme.spacing(3),
  color: 'white',
}));

const StatusMessageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: theme.spacing(2.5),
}));


// Continuous loading progress bar
const LoadingProgressBar = styled(Box)(({ theme, status }) => {
  const getAnimation = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return `${shimmer} 2s infinite linear`;
      case 'PREPARING':
        return `${wave} 1.5s infinite ease-in-out`;
      case 'READY':
        return `${glow} 2s infinite ease-in-out`;
      case 'COMPLETED':
        return `${completionPulse} 3s infinite ease-in-out`;
      default:
        return `${shimmer} 2s infinite linear`;
    }
  };

  return {
    height: 12,
    borderRadius: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: status?.toUpperCase() === 'COMPLETED'
        ? 'rgba(255, 255, 255, 0.9)'
        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
      animation: status?.toUpperCase() === 'COMPLETED'
        ? `${completionPulse} 3s infinite ease-in-out`
        : getAnimation(status),
      borderRadius: 6,
    },
    '&::after': status?.toUpperCase() === 'PREPARING' ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
      animation: `${shimmer} 1s infinite linear`,
      borderRadius: 6,
      animationDelay: '0.5s',
    } : {},
  };
});

const CurrentStatusContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: 12,
  backdropFilter: 'blur(10px)',
}));

const CurrentStatusLabel = styled(Typography)({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.9)',
  marginBottom: 8,
});

const CurrentStatusDescription = styled(Typography)({
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.8)',
  lineHeight: 1.5,
  maxWidth: '300px',
  margin: '0 auto',
});

const MainStatusMessage = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: '#2c3e50',
  marginBottom: theme.spacing(1),
  textAlign: 'center',
}));

const SubStatusMessage = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#64748b',
  textAlign: 'center',
  lineHeight: 1.5,
}));

const HorizontalProgressBar = ({ order }) => {
  const { primaryColor, fontFamily } = usePublicTheme();
  const [currentStatus, setCurrentStatus] = useState(order?.status || 'PENDING');

  // Track status changes
  useEffect(() => {
    if (order?.status && order.status !== currentStatus) {
      setCurrentStatus(order.status);
    }
  }, [order?.status, currentStatus]);

  // Define the order status steps
  const orderSteps = [
    {
      status: 'PENDING',
      label: 'Order Received',
      description: 'Your order has been received and is being processed by our team'
    },
    {
      status: 'PREPARING',
      label: 'Preparing',
      description: 'Our kitchen team is carefully preparing your delicious order'
    },
    {
      status: 'READY',
      label: 'Ready',
      description: 'Your order is ready! Please come to the pickup counter'
    },
    {
      status: 'COMPLETED',
      label: 'Completed',
      description: 'Order completed successfully. Thank you for choosing us!'
    }
  ];

  // Get current status info with better matching
  const getCurrentStatusInfo = () => {
    if (!order?.status) {
      return {
        label: 'Loading Order',
        description: 'Please wait while we fetch your order information...'
      };
    }

    // Normalize status for comparison
    const normalizedStatus = order.status?.toUpperCase();

    // Try exact match first
    const currentStep = orderSteps.find(step => step.status === normalizedStatus);
    if (currentStep) {
      return currentStep;
    }

    // Try partial matching for common variations
    switch (normalizedStatus) {
      case 'CANCELLED':
      case 'CANCELED':
        return {
          label: 'Order Cancelled',
          description: order.cancellation_reason || 'This order has been cancelled.'
        };
      case 'PENDING':
      case 'RECEIVED':
      case 'PLACED':
        return {
          label: 'Order Received',
          description: 'Your order has been received and is being processed by our team'
        };
      case 'PREPARING':
      case 'COOKING':
      case 'IN_PROGRESS':
        return {
          label: 'Preparing',
          description: 'Our kitchen team is carefully preparing your delicious order'
        };
      case 'READY':
      case 'PREPARED':
      case 'READY_FOR_PICKUP':
        return {
          label: 'Ready',
          description: 'Your order is ready! Please come to the pickup counter'
        };
      case 'COMPLETED':
      case 'DELIVERED':
      case 'PICKED_UP':
        return {
          label: 'Completed',
          description: 'Order completed successfully. Thank you for choosing us!'
        };
      default:
        // Log unknown status for debugging
        console.log('Unknown order status:', order.status);
        return {
          label: 'Processing Order',
          description: 'Tracking your order status...'
        };
    }
  };

  const isCancelled = order?.status === 'CANCELLED';
  const isLoading = !order?.status;
  const currentStatusInfo = getCurrentStatusInfo();

  if (isLoading) {
    return (
      <ProgressContainer>
        <GradientHeader primarycolor={primaryColor}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1, fontFamily }}>
              Loading Order Details
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.875rem', fontFamily }}>
              Please wait while we fetch your order information...
            </Typography>
          </Box>

          {/* Loading Progress Bar */}
          <LoadingProgressBar status="PENDING" />

          {/* Loading Status Information */}
          <CurrentStatusContainer>
            <CurrentStatusLabel>
              Loading Order
            </CurrentStatusLabel>
            <CurrentStatusDescription>
              Please wait while we fetch your order information...
            </CurrentStatusDescription>
          </CurrentStatusContainer>
        </GradientHeader>
      </ProgressContainer>
    );
  }

  if (isCancelled) {
    return (
      <ProgressContainer>
        <GradientHeader sx={{ background: 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)' }}>
          <Box textAlign="center">
            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto'
            }}>
              <CancelIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
              Order Cancelled
            </Typography>
          </Box>
        </GradientHeader>
        <StatusMessageContainer>
          <MainStatusMessage sx={{ color: '#e53e3e' }}>
            Order Cancelled
          </MainStatusMessage>
          <SubStatusMessage>
            {order?.cancellation_reason || 'This order has been cancelled.'}
          </SubStatusMessage>
        </StatusMessageContainer>
      </ProgressContainer>
    );
  }

  return (
    <ProgressContainer>
      <GradientHeader primarycolor={primaryColor}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 1, fontFamily }}>
            Order Progress
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.875rem', fontFamily }}>
            Track your order status in real-time
          </Typography>
        </Box>

        {/* Continuous Loading Progress Bar */}
        <LoadingProgressBar status={order?.status || 'PENDING'} />

        {/* Current Status Information */}
        <CurrentStatusContainer>
          <CurrentStatusLabel>
            {currentStatusInfo.label}
          </CurrentStatusLabel>
          <CurrentStatusDescription>
            {currentStatusInfo.description}
          </CurrentStatusDescription>
        </CurrentStatusContainer>
      </GradientHeader>
    </ProgressContainer>
  );
};

export default HorizontalProgressBar;
